/**
 * Generic D3 Sankey LiveView hook.
 *
 * Mount on any element with:
 *   phx-hook="Sankey"
 *   data-nodes='[{"name":"...","color":"#hex"}]'   ← required
 *   data-links='[{"source":0,"target":1,"value":N}]' ← required
 *   data-options='{"height":420,"nodeWidth":18,...}' ← optional overrides
 *
 * Nodes may carry an optional `color` field. If absent, the hook picks from
 * the default palette by node index.
 *
 * The hook re-renders on `updated()` so LiveView can push new data by updating
 * the data attributes (e.g. when the user switches a time-window pill).
 */

import { sankey, sankeyLeft, sankeyLinkHorizontal } from "d3-sankey";
import { select } from "d3-selection";

const PALETTE = [
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
  "#3b82f6",
  "#14b8a6",
  "#f43f5e",
];

const DEFAULTS = {
  height: 420,
  marginTop: 16,
  marginRight: 220,
  marginBottom: 40,
  marginLeft: 140,
  nodeWidth: 18,
  nodePadding: 12,
  linkOpacity: 0.28,
  labelFontSize: 12,
  subLabelFontSize: 10,
  axisLabelFontSize: 9,
};

const fmt = (n) => Math.round(n).toLocaleString("en-US");

function renderSankey(container, nodes, links, opts = {}) {
  const o = { ...DEFAULTS, ...opts };

  select(container).selectAll("*").remove();
  if (!nodes.length || !links.length) return;

  const containerWidth = container.getBoundingClientRect().width || 900;
  const W = Math.max(600, containerWidth);
  const IW = W - o.marginLeft - o.marginRight;
  const IH = o.height - o.marginTop - o.marginBottom;

  // ── Sankey layout ───────────────────────────────────────────────────────
  const { nodes: ln, links: ll } = sankey()
    .nodeAlign(sankeyLeft)
    .nodeWidth(o.nodeWidth)
    .nodePadding(o.nodePadding)
    .extent([
      [0, 0],
      [IW, IH],
    ])({
    nodes: nodes.map((d) => ({ ...d })),
    links: links.map((d) => ({ ...d })),
  });

  // Assign colors: prefer node.color, fall back to palette by index
  const colored = ln.map((n, i) => ({
    ...n,
    color: n.color ?? PALETTE[i % PALETTE.length],
  }));

  // ── SVG ─────────────────────────────────────────────────────────────────
  const svg = select(container)
    .append("svg")
    .attr("width", W)
    .attr("height", o.height)
    .attr("viewBox", `0 0 ${W} ${o.height}`)
    .style("max-width", "100%")
    .style("font-family", "inherit")
    .style("overflow", "visible");

  const g = svg.append("g").attr("transform", `translate(${o.marginLeft},${o.marginTop})`);

  // Links
  g.append("g")
    .selectAll("path")
    .data(ll)
    .join("path")
    .attr("d", sankeyLinkHorizontal())
    .attr("fill", "none")
    .attr("stroke", (d) => colored[d.source.index].color)
    .attr("stroke-opacity", o.linkOpacity)
    .attr("stroke-width", (d) => Math.max(1, d.width));

  // Node rectangles
  g.append("g")
    .selectAll("rect")
    .data(colored)
    .join("rect")
    .attr("x", (d) => d.x0)
    .attr("y", (d) => d.y0)
    .attr("width", (d) => d.x1 - d.x0)
    .attr("height", (d) => Math.max(2, d.y1 - d.y0))
    .attr("fill", (d) => d.color)
    .attr("rx", 2);

  // Node labels (name + sub-label)
  const labelG = g.append("g");

  colored.forEach((n) => {
    const cy = (n.y0 + n.y1) / 2;
    const isLeft = n.depth === 0;
    const x = isLeft ? n.x0 - 10 : n.x1 + 10;
    const anchor = isLeft ? "end" : "start";

    // Primary label
    labelG
      .append("text")
      .attr("x", x)
      .attr("y", cy - 7)
      .attr("dy", "0.35em")
      .attr("text-anchor", anchor)
      .attr("font-size", o.labelFontSize)
      .attr("font-weight", "600")
      .attr("fill", "#374151")
      .text(n.name);

    // Sub-label (label2 if provided, otherwise value + pct-of-root)
    const subText = n.label2 ?? buildSubLabel(n, colored, o);
    if (subText) {
      labelG
        .append("text")
        .attr("x", x)
        .attr("y", cy + 10)
        .attr("text-anchor", anchor)
        .attr("font-size", o.subLabelFontSize)
        .attr("fill", "#9ca3af")
        .text(subText);
    }
  });

  // Axis labels (use node.axisLabel on nodes that should label a column)
  colored.forEach((n) => {
    if (!n.axisLabel) return;
    g.append("text")
      .attr("x", (n.x0 + n.x1) / 2)
      .attr("y", IH + 28)
      .attr("text-anchor", "middle")
      .attr("font-size", o.axisLabelFontSize)
      .attr("font-weight", "700")
      .attr("fill", "#9ca3af")
      .attr("letter-spacing", "0.06em")
      .text(n.axisLabel);
  });
}

// Default sub-label: "1,234 · 56.7%"
function buildSubLabel(node, colored, _o) {
  const root = colored[0];
  if (!root || root.value === 0) return fmt(node.value);
  const pct = ((node.value / root.value) * 100).toFixed(1) + "%";
  return `${fmt(node.value)}  ·  ${pct}`;
}

// ── LiveView hook ────────────────────────────────────────────────────────────

export const Sankey = {
  mounted() {
    this._render();
    this._resizeObserver = new ResizeObserver(() => this._render());
    this._resizeObserver.observe(this.el);
  },

  updated() {
    this._render();
  },

  destroyed() {
    if (this._resizeObserver) this._resizeObserver.disconnect();
  },

  _render() {
    try {
      const nodes = JSON.parse(this.el.dataset.nodes || "[]");
      const links = JSON.parse(this.el.dataset.links || "[]");
      const options = JSON.parse(this.el.dataset.options || "{}");
      renderSankey(this.el, nodes, links, options);
    } catch (e) {
      console.error("[Sankey] failed to render", e);
    }
  },
};
